import { Icon } from '@design';
import {
  checkCellValueStyle,
  formatPercentage,
  NumericalCellValueStyle,
} from '@helpers';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './InsightCardDetails.module.scss';

const numericalStyleClass: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: styles.positive,
  [NumericalCellValueStyle.Negative]: styles.negative,
  [NumericalCellValueStyle.Neutral]: styles.neutral,
};

export interface InsightCardDetailsProps {
  qualityStars?: number;
  horizon?: string;
  median: number;
  isFixedIncome?: boolean;
}

export const InsightCardDetails = ({
  qualityStars,
  horizon,
  median,
  isFixedIncome = false,
}: InsightCardDetailsProps) => {
  const { t } = useTranslation('widget');
  const suffix = isFixedIncome ? 'bps' : '%';
  const decimals = isFixedIncome ? 0 : 2;

  const valueStyle = checkCellValueStyle(median);

  const starsTitleMapping = (numberOfStars: number): string => {
    switch (numberOfStars) {
      case 0:
      case 1:
      case 2:
        return t('widget:insight.starsTitle.0_2_stars');
      case 3:
      case 4:
        return t('widget:insight.starsTitle.3_4_stars');
      case 5:
        return t('widget:insight.starsTitle.5_stars');
      case 6:
        return t('widget:insight.starsTitle.6_stars');
      case 7:
        return t('widget:insight.starsTitle.7_stars');
      case 8:
        return t('widget:insight.starsTitle.8_stars');
      default:
        return '';
    }
  };

  return (
    <div className={styles.details}>
      {qualityStars && (
        <>
          <div className={styles.detail}>
            <p className={styles.heading}>{starsTitleMapping(qualityStars)}</p>
            <div className={styles.groupedText}>
              <Icon
                iconName="StarFill"
                fillColor="var(--icon-primary)"
                size={20}
              />
              <p className={styles.text}>{qualityStars}&nbsp;/&nbsp;8</p>
            </div>
          </div>
          <hr className={styles.separator} />
        </>
      )}
      {horizon && (
        <>
          <div className={styles.detail}>
            <p className={styles.heading}>
              {t('widget:insight.attractiveHorizon')}
            </p>
            <p className={styles.textUpper}>
              {
                /* i18next-extract-mark-context-next-line ["1m", "1w", "2w", "3m", "6m"] */
                t('widget:assetOverview.horizon', {
                  context: horizon,
                })
              }
            </p>
          </div>
          <hr className={styles.separator} />
        </>
      )}
      <div className={styles.detail}>
        <p className={styles.heading}>{t('widget:insight.medianReturn')}</p>
        <p
          className={clsx(
            styles.detailReturn,
            numericalStyleClass[valueStyle]
          )}
        >
          {valueStyle === NumericalCellValueStyle.Positive && '+'}
          {formatPercentage(median, {
            suffix,
            decimals,
          })}
        </p>
      </div>
    </div>
  );
};
